import React, { useState, useEffect, useRef } from 'react';
import { 
  BookOpen, Menu, X, Play, ArrowRight, Target, Zap, Brain, FileText, Mail
} from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
// Main Home Page Component
export default function Home({ setIsLoggedIn }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [animate, setAnimate] = useState(false);
  const API_BASE_URL = 'https://zimsyai-backend.onrender.com'; 
  useEffect(() => {
    // Run animation on mount
    setAnimate(true);
  }, []);
  // --- Add all of this ---

// 1. This function sends the token to your backend
    const handleBackendLogin = async (token) => {
    try {
        const res = await fetch(`${API_BASE_URL}/api/google-login`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: token }), // Send the token
        });

        if (res.ok) {
        const data = await res.json();
        console.log("Backend login successful:", data.email);
        localStorage.setItem('isLoggedIn', 'true'); // <-- ADD THIS
        localStorage.setItem('userEmail', data.email);
        localStorage.setItem('userId', data.user_id); // Or whatever the email field is
        setIsLoggedIn(true); // Triggers redirect in App.js
        } else {
        const err = await res.json();
        console.error("Backend login failed:", err.error);
        alert("Login failed. Please try again.");
        }
    } catch (error) {
        console.error("Error sending token to backend:", error);
        alert("An error occurred during login.");
    }
    };

    // 2. This is the onSuccess handler for the hook
    // It receives the token from Google and passes it to our backend function
    const handleLoginSuccess = (tokenResponse) => {
    // We send the 'access_token' to our backend
    handleBackendLogin(tokenResponse.access_token);
    };

    const handleLoginError = () => {
    console.error("Google Login Failed");
    alert("Google login failed. Please try again.");
    };

    // 3. This is the hook. It gives us a function to trigger the login.
    const googleLogin = useGoogleLogin({
    onSuccess: handleLoginSuccess,
    onError: handleLoginError,
    });
// -------------------------
  return (
    <div className="flex min-h-screen w-full flex-col bg-neutral-900 text-neutral-100 font-sans">
      
      {/* Navbar */}
      <Navbar 
        isMobileMenuOpen={isMobileMenuOpen} 
        setIsMobileMenuOpen={setIsMobileMenuOpen} 
        onStartNowClick={googleLogin}  
        />
            
      {/* Main Content */}
      <main className="flex-1">
        <HeroSection 
        animate={animate} 
        onStartNowClick={googleLogin} 
        />
        <FeaturesSection />
        <AboutSection />
        <ContactSection />
      </main>

      {/* Footer */}
      <Footer />
      
      {/* CSS Animations */}
      <style>{`
        @keyframes slideUpWord {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slideUpFade {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slideDownFade {
          from { opacity: 0; transform: translateY(-30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        /* Added for mobile menu */
        @keyframes slideInFromTop {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-slideUpWord {
          animation: slideUpWord 0.8s ease-out forwards;
          opacity: 0;
        }

        .animate-slideUpFade {
          animation: slideUpFade 0.8s ease-out forwards;
          opacity: 0;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        
        .animate-slideInFromTop {
          animation: slideInFromTop 0.4s ease-out forwards;
        }
        
        /* New animation for robot */
        @keyframes popIn {
            0% { opacity: 0; transform: scale(0.5); }
            80% { opacity: 1; transform: scale(1.1); }
            100% { opacity: 1; transform: scale(1); }
        }
        .animate-popIn {
            animation: popIn 0.6s ease-out forwards;
            opacity: 0;
        }
      `}</style>
    </div>
  );
}

// --- Navbar ---
function Navbar({ isMobileMenuOpen, setIsMobileMenuOpen, onStartNowClick }) {
  const navItems = [
    { name: 'Home', href: '#' },
    { name: 'Features', href: '#features' },
    { name: 'About', href: '#about' },
    { name: 'Contact', href: '#contact' },
  ];

  return (
    <>
      <header className="sticky top-0 z-30 w-full border-b border-white/10 bg-neutral-900/80 backdrop-blur-md">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg ">
              {/* Using the book image as the logo */}
              <img src="/image/logo-png.png" alt="ZimsyAI Logo" className="h-full w-full object-contain rounded-lg" />
            </div>
            <span className="text-2xl font-bold text-white">ZimsyAI</span>
          </a>
          
          {/* Desktop Nav */}
          <nav className="hidden items-center gap-10 md:flex">
            <div className="flex items-center gap-6">
              {navItems.map((item) => (
                <a 
                  key={item.name} 
                  href={item.href} 
                  className="text-sm font-medium text-neutral-300 transition-colors hover:text-white"
                >
                  {item.name}
                </a>
              ))}
            </div>
            <div className="flex items-center gap-4">
              <button 
                onClick={() => onStartNowClick()} 
                className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-neutral-900 transition-transform hover:scale-105"
                >
                Start Now
                </button>
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="text-neutral-300 md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>
      
      {/* Mobile Menu (Dropdown) */}
      {isMobileMenuOpen && (
        <div 
              className="fixed top-20 left-0 z-50 w-full h-[calc(100vh-80px)] overflow-y-auto 
                        flex-col border-b border-white/10 bg-neutral-900 p-6 shadow-xl 
                        animate-slideInFromTop md:hidden"
            >

          <nav className="flex flex-col gap-6">
            {navItems.map((item) => (
              <a 
                key={item.name} 
                href={item.href} 
                className="text-base font-medium text-neutral-100"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.name}
              </a>
            ))}
          </nav>
          <div className="mt-8 flex flex-col gap-4 border-t border-neutral-700 pt-6">
            <div className="flex justify-center">
            <button 
                onClick={() => onStartNowClick()} 
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-white py-2.5 text-sm font-semibold text-neutral-900"
                >
                Start Now
                </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// --- Hero Section ---
function HeroSection({ animate, onStartNowClick }) {
  const headingWords = ["Learn", "Smarter,", "Revise", "Faster.", "Zimsy's", "Got", "You."];
  
  return (
    <section className="relative overflow-hidden pt-24 pb-28 md:pt-32 md:pb-40">
      {/* Background Glow */}
      <div className="absolute -top-1/4 left-1/2 -z-0 h-1/2 w-1/2 -translate-x-1/2 rounded-full bg-purple-900/30 blur-[120px]"></div>
      
      {/* Container changed from max-w-4xl to max-w-7xl to accommodate two columns */}
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {/* Grid layout: 1 column on mobile, 2 columns on medium screens+ */}
        <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2">

          {/* --- Left Column (Text Content) --- */}
          {/* Aligns text center on mobile, and left on desktop */}
          <div className="text-center md:text-left">
            <h1 className="text-4xl font-extrabold tracking-tight text-white md:text-6xl">
              {headingWords.map((word, i) => (
                <span
                  key={i}
                  className={`inline-block ${word === "Zimsy's" || word === "Got" || word === "You." ? 'text-purple-400' : ''} ${animate ? 'animate-slideUpWord' : ''}`}
                  style={{ 
                    animationDelay: `${i * 0.15}s`,
                    opacity: animate ? undefined : 1,
                    marginRight: '0.5rem',
                  }}
                >
                  {word}
                </span>
              ))}
            </h1>
            <p 
              // Removed mx-auto, as the parent div handles alignment
              className={`mt-6 max-w-2xl text-lg text-neutral-300 md:text-xl ${animate ? 'animate-slideUpFade' : ''}`}
              style={{ animationDelay: '1.2s', opacity: animate ? undefined : 1 }}
            >
              Zimsy AI creates AI-assisted, syllabus-ready videos that turn hours of study into minutes of clarity.
              Perfect for quick revision or brushing up before tests.
            </p>
            <div 
              // Aligns buttons center on mobile/small, and left on medium+
              className={`mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center md:justify-start ${animate ? 'animate-slideUpFade' : ''}`}
              style={{ animationDelay: '1.4s', opacity: animate ? undefined : 1 }}
            >
              <button 
                onClick={() => onStartNowClick()} 
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-white px-6 py-3 text-base font-semibold text-neutral-900 transition-transform hover:scale-105 sm:w-auto"
                >
                Start Now <ArrowRight size={18} />
                </button>
            </div>
          </div>

          {/* --- Right Column (Image) --- */}
          {/* Added margin-top on mobile, removed on desktop (md:mt-0) */}
          <div className="mt-12 md:mt-0">
            <div 
                className={`flex justify-center ${animate ? 'animate-popIn' : ''}`}
                style={{ animationDelay: '1.6s', opacity: animate ? undefined : 0 }}
            >
                {/* Increased image size on desktop for better balance */}
                <img src="/image/book_robot.png" alt="Zimsy AI Robot" className="h-40 w-40 object-contain md:h-64 md:w-64 lg:h-80 lg:w-80" />
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

// Custom hook for scroll-triggered animations
function useScrollAnimation() {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return [ref, isVisible];
}

// --- Features Section ---
const features = [
  {
    icon: <Target size={24} className="text-purple-400" />,
    title: "AI-Tailored Lessons",
    description: "Content personalized to your syllabus, ensuring you learn exactly what you need.",
    delay: 0.2
  },
  {
    icon: <Zap size={24} className="text-blue-400" />,
    title: "Fast, Focused Learning",
    description: "Cover entire chapters in minutes with our smartly structured one-shot video experience.",
    delay: 0.5
  },
  {
    icon: <Brain size={24} className="text-green-400" />,
    title: "Comprehensive Clarity",
    description: "Get clear, simple explanations that feel like a personal tutor, breaking down complex topics.",
    delay: 0.8
  },
  {
    icon: <FileText size={24} className="text-yellow-400" />,
    title: "Instant PDF Notes",
    description: "Download neatly summarized PDF notes right after the video for quick and easy revision.",
    delay: 1
  },
];

function FeaturesSection() {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <section id="features" className="bg-neutral-950 py-20 md:py-28" ref={ref}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white md:text-4xl">
            Learn Smarter, Not Harder
          </h2>
          <p className="mt-4 text-lg text-neutral-400">
            Everything you need for last-minute revision, all in one place.
          </p>
        </div>
        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {features.map((feature) => (
            <FeatureCard 
              key={feature.title} 
              feature={feature} 
              animate={isVisible} 
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ feature, animate }) {
  return (
    <div 
      className={`flex flex-col rounded-2xl border border-neutral-800 bg-neutral-900 p-6 transition-all hover:border-purple-500/50 hover:shadow-lg ${animate ? 'animate-slideUpFade' : ''}`}
      style={{ 
        animationDelay: `${feature.delay}s`,
        opacity: animate ? undefined : 1
      }}
    >
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-neutral-800">
        {feature.icon}
      </div>
      <h3 className="mb-2 text-xl font-semibold text-white">{feature.title}</h3>
      <p className="text-neutral-400">{feature.description}</p>
    </div>
  );
}

// --- About Section ---
function AboutSection() {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <section id="about" className="py-20 md:py-28" ref={ref}>
      <div 
        className={`mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 ${isVisible ? 'animate-slideUpFade' : ''}`}
        style={{ animationDelay: '0.2s', opacity: isVisible ? undefined : 1 }}
      >
        <h2 className="mb-8 text-center text-3xl font-bold tracking-tight text-white md:text-4xl">
          About Zimsy AI
        </h2>
        <div className="flex flex-col gap-5 text-left text-lg text-neutral-300">
        <p>
          Zimsy AI is a free learning platform designed to help students revise faster and smarter through concise, one-shot video lessons and easy-to-understand notes.
        </p>

        <p>
          Our goal is simple — to make syllabus completion stress-free, time-efficient, and engaging for every learner who has little time but a vast syllabus pending.
        </p>

        <p>
          Currently, we offer selected course videos completely free so that students can experience how Zimsy AI helps in rapid concept brushing and last-minute revision.
        </p>

        <p>
          If your subject isn’t available yet, you can request it directly from your dashboard.  
          Just provide the subject name and a detailed syllabus, and we will add it to our creation queue.
        </p>

        <p className="rounded-lg border border-purple-500/30 bg-purple-900/20 p-4 text-purple-200">
          All requests follow a first-come, first-served queue system — the earliest request gets completed first.  
          If you have an urgent requirement or need your subject on priority, please email us at  
          <span className="font-semibold text-white"> zimsyai@gmail.com</span>.
        </p>

        <p>
          Zimsy AI currently uses AI-assisted content generated on our local setup. The AI engine isn’t integrated into this website yet, and the platform is still a prototype under active development — soon to become a fully AI-powered learning assistant.
        </p>

        <p>
          We believe education should be accessible to everyone, and Zimsy AI will always remain free for learning and exploration.
        </p>

        <p>
          💡 If you believe in this vision and wish to support or invest in its growth, feel free to   
          <a href="#contact" className="font-semibold text-purple-300 underline hover:text-purple-200"> reach out to us</a>.  
          Together, we can shape the future of quick and intelligent learning.
        </p>

        </div>
      </div>
    </section>
  );
}

// --- Contact Section ---
function ContactSection() {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <section id="contact" className="bg-neutral-950 py-20 md:py-28" ref={ref}>
      <div 
        className={`mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8 ${isVisible ? 'animate-slideUpFade' : ''}`}
        style={{ animationDelay: '0.2s', opacity: isVisible ? undefined : 1 }}
      >
        <h2 className="text-3xl font-bold tracking-tight text-white md:text-4xl">
          Get in Touch
        </h2>
        <p className="mt-4 text-lg text-neutral-400">
          Have questions, feedback, or want to get involved? We'd love to hear from you.
        </p>
        <a 
          href="mailto:zimsyai@gmail.com"
          className="mt-8 inline-flex items-center gap-3 rounded-lg bg-purple-600 px-6 py-3 text-base font-semibold text-white transition-transform hover:scale-105 hover:bg-purple-500"
        >
          <Mail size={20} />
          zimsyai@gmail.com
        </a>
      </div>
    </section>
  );
}

// --- Footer ---
function Footer() {
  return (
    <footer className="border-t border-white/10 py-8">
      <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
        <p className="text-sm text-neutral-500">
          © {new Date().getFullYear()} ZimsyAI. All rights reserved.
        </p>
      </div>
    </footer>
  );

}
